import React, {MouseEvent, ReactNode, useCallback, useState} from 'react';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {INotifyChannel} from 'entities/notification';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import NotifyButton from 'shared/ui/Button/NotifyButton/NotifyButton';
import {INotifyChannelApi} from 'features/profile/model/notifyChannelApi';
import useTestNotify from 'features/profile/model/useTestNotify';

import {ActionCells, DescriptionCell} from './NotifyChannelCells';
import NotifyChannelStatusIcon from './NotifyChannelStatusIcon';

import styles from './NotifyChannels.module.scss';

export interface INotifyChannelModalProps {
    open: boolean,
    handleClose: () => void,
}

interface INotifyChannelRowProps<T extends INotifyChannel> {
    item: T,
    title: string,
    api: INotifyChannelApi<T>,
    activatedText: string,
    deactivatedText: string,
    renderEditModal: (_props: INotifyChannelModalProps) => ReactNode,
    renderDeleteModal: (_props: INotifyChannelModalProps) => ReactNode,
}

const NotifyChannelRow = <T extends INotifyChannel>({
    item,
    title,
    api,
    activatedText,
    deactivatedText,
    renderEditModal,
    renderDeleteModal,
}: INotifyChannelRowProps<T>) => {
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);

    const [update] = api.useUpdateMutation();
    const [sendTestNotify] = api.useSendTestNotifyMutation();

    const testNotify = useTestNotify(item.active, () => sendTestNotify(item.id));

    const onClickToggleStatus = useCallback(async(event: MouseEvent) => {
        event.stopPropagation();
        const result = await update({id: item.id, active: !item.active});
        if ('error' in result) return;
        toast.info(item.active ? deactivatedText : activatedText);
    }, [item.active, item.id, update, activatedText, deactivatedText]);

    const openEdit = () => setIsModalEditOpen(true);

    return (
        <tr className={styles.row}>
            <td>
                <div className={styles.containerTitle}>
                    <NotifyChannelStatusIcon active={item.active}/>
                    {title}
                </div>
            </td>
            <DescriptionCell description={item.description} placeholder={t`Не заполнено`} onEdit={openEdit}/>
            <td>
                <StatusToggle
                    condition={item.active}
                    onClick={onClickToggleStatus}
                />
            </td>
            <td>
                <NotifyButton
                    loading={testNotify.sending}
                    disabled={testNotify.disabled}
                    onClick={testNotify.onSend}
                />
            </td>
            <ActionCells onEdit={openEdit} onDelete={() => setIsModalDeleteOpen(true)}/>
            {renderEditModal({open: isModalEditOpen, handleClose: () => setIsModalEditOpen(false)})}
            {renderDeleteModal({open: isModalDeleteOpen, handleClose: () => setIsModalDeleteOpen(false)})}
        </tr>
    );
};

export default NotifyChannelRow;
